export const WEEK_DAYS = [
  { label: 'M', key: 'M' },
  { label: 'T', key: 'T' },
  { label: 'W', key: 'W' },
  { label: 'TH', key: 'TH' },
  { label: 'F', key: 'F' },
];

export const getStoredTeacher = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

export const getSchoolId = (teacher = getStoredTeacher()) => {
  const school = teacher?.school;
  if (!school) return teacher?.schoolId || '';
  if (typeof school === 'string') return school;
  return school._id || school.id || school.schoolId || teacher?.schoolId || '';
};

export const getTeacherId = (teacher = getStoredTeacher()) =>
  teacher?.teacherId || teacher?.teacher?._id || teacher?._id || teacher?.id || '';

export const getClassDisplayName = (cls) => {
  if (!cls) return 'Class';
  if (typeof cls.classDisplayName === 'string' && cls.classDisplayName.trim()) {
    return cls.classDisplayName;
  }
  if (typeof cls.displayName === 'string' && cls.displayName.trim()) {
    return cls.displayName;
  }
  if (cls.name && cls.stream) return `${cls.name}${cls.stream}`;
  return cls.name || 'Class';
};

export const getApiErrorMessage = (error, fallback = 'Something went wrong.') =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

export const formatTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const toInputDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export const getBrowserDeviceId = () => {
  const key = 'teacherPortalDeviceId';
  const existing = localStorage.getItem(key);
  if (existing) return existing;

  const generated =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `teacher-web-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  localStorage.setItem(key, generated);
  return generated;
};

const getGeolocationErrorMessage = (error) => {
  if (!error) return 'Unable to get your location. Please try again.';

  if (error.code === 1) {
    return 'Location permission is blocked. Allow location access for this site in your browser settings and try again.';
  }

  if (error.code === 2) {
    return 'Your browser could not get your current location. Turn on device location/GPS, keep Wi-Fi or mobile data enabled, then try again.';
  }

  if (error.code === 3) {
    return 'Getting your location took too long. Move to an open area or check your internet connection, then try again.';
  }

  return error.message || 'Unable to get your location. Please try again.';
};

const requestBrowserPosition = (options) =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

const requestBrowserWatchedPosition = (options, waitMs = 35000) =>
  new Promise((resolve, reject) => {
    let watchId;
    let settled = false;
    let bestPosition = null;
    let lastError = null;

    const finish = (handler, value) => {
      if (settled) return;
      settled = true;
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
      handler(value);
    };

    const timer = setTimeout(() => {
      if (bestPosition) {
        finish(resolve, bestPosition);
        return;
      }
      finish(reject, lastError || new Error('Getting your location took too long.'));
    }, waitMs);

    const cleanupAndFinish = (handler, value) => {
      clearTimeout(timer);
      finish(handler, value);
    };

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        bestPosition = position;
        const accuracy = Number(position.coords?.accuracy || 0);
        if (!accuracy || accuracy <= 150) {
          cleanupAndFinish(resolve, position);
        }
      },
      (error) => {
        lastError = error;
      },
      options
    );
  });

export const getBrowserLocation = async () => {
  if (!navigator.geolocation) {
    throw new Error('This browser does not support location services.');
  }

  if (window.isSecureContext === false) {
    throw new Error('Browser location requires a secure HTTPS connection. Open the teacher portal with HTTPS and try again.');
  }

  const attempts = [
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 },
    { enableHighAccuracy: true, timeout: 35000, maximumAge: 0, watch: true },
  ];

  let lastError;
  for (const options of attempts) {
    try {
      const { watch, ...geoOptions } = options;
      return watch
        ? await requestBrowserWatchedPosition(geoOptions)
        : await requestBrowserPosition(geoOptions);
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(getGeolocationErrorMessage(lastError));
};

export const resolveArray = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

export const teacherPortalGridItemSx = {
  display: 'flex',
  minWidth: 0,
};

export const teacherPortalCardSx = (theme, overrides = {}) => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  backgroundImage: 'none',
  ...overrides,
});

export const teacherPortalPanelCardSx = (theme, overrides = {}) =>
  teacherPortalCardSx(theme, {
    height: '100%',
    minHeight: { xs: 'auto', md: 520 },
    ...overrides,
  });

export const teacherPortalStatCardSx = (theme, overrides = {}) =>
  teacherPortalCardSx(theme, {
    height: 116,
    display: 'flex',
    ...overrides,
  });

export const teacherPortalTableCardSx = (theme, overrides = {}) =>
  teacherPortalCardSx(theme, {
    overflow: 'hidden',
    ...overrides,
  });

export const teacherPortalRowSx = (theme, overrides = {}) => ({
  p: 1.5,
  borderRadius: 1,
  border: `1px solid ${theme.palette.divider}`,
  ...overrides,
});
